import { HttpParams } from '@angular/common/http';
import {
  CanActivateFn,
  RedirectFunction,
  Route,
  Routes
} from '@angular/router';

import { AdminTabRoute } from 'src/app/features/admin/container/axle-admin-container.component';
import { AdminEmailsTab } from 'src/app/features/admin/emails/axle-admin-emails.component';
import { TeamsAndGroupsTabRoute } from 'src/app/features/admin/user-management/teams-and-groups/axle-admin-teams-and-groups.component';
import { CRMTabRoute } from 'src/app/features/crm/container/axle-crm-container.component';
import { EmailTabRoute } from 'src/app/features/email/container/axle-email-container.component';
import { EmailOneTimeTabRoute } from 'src/app/features/email/one-time/container/axle-email-one-time-container.component';
import { EmailRecurringTabRoute } from 'src/app/features/email/recurring/container/axle-email-recurring-container.component';
import { EmailNotificationTabRoute } from 'src/app/features/events/configure/notification-emails/axle-event-configure-notification-emails.component';
import { EventDetailsTabRoute } from 'src/app/features/events/details/container/axle-events-details.component';
import { ScheduleAdditionsTab } from 'src/app/features/events/itinerary/schedule-additions/axle-schedule-additions.models';
import { ConfigureEventTabRoute } from 'src/app/features/events/manage/axle-events-manage.component';
import {
  SCHEDULE_EVENT_ACTIVITY_LOG_ROUTE,
  SCHEDULE_EVENT_EVENT_CHANGES_ROUTE,
  SCHEDULE_EVENT_ID_ROUTE,
  SCHEDULE_EVENT_PRESENTATIONS_ROUTE,
  SCHEDULE_EVENT_REPORTING_ROUTE,
  SCHEDULE_EVENT_REQUEST_UPDATES_ROUTE,
  SCHEDULE_EVENT_SCHEDULER_ROUTE
} from 'src/app/features/events/schedule/schedule-event.constants';
import { IntelligenceHubTabRoute } from 'src/app/features/intelligence-hub/container/axle-intelligence-hub-container.component';
import { IntelligenceHubScorecardTab } from 'src/app/features/intelligence-hub/scorecard/container/axle-intelligence-hub-scorecard-container.component';
import {
  determineAuthRedirectTo,
  determineRedirectTo,
  handleInvitationSignup,
  parentAuthGuardFn,
  redirectIfAuthenticated,
  redirectToAxleAccessDomain
} from 'src/app/shared/guard/authentication.guards';
import { clearContactsStore } from 'src/app/shared/guard/contacts.guards';
import { canDeactivateGuard } from 'src/app/shared/guard/deactivate-component.guards';
import { clearEmailStore } from 'src/app/shared/guard/email-campaign.guards';
import {
  canViewAllocationManagerGuard,
  canViewPresentationsGuard,
  canViewScheduleEventPresentationsGuard,
  clearEventsStore,
  clearScheduleEventStore,
  confirmUpdateEventScheduleModeUnsavedChangesGuard,
  ensureEventDetailsLoadedGuard,
  ensureEventTemplateLoadedGuard,
  ensureScheduleEventDetailsLoadedGuard,
  stripUpdateEventScheduleModeModalForNonCoordinatorGuard,
  supportsRequestableAdditionsGuard,
  syncEventDetailsGuard
} from 'src/app/shared/guard/events.guards';
import { integrationsEnabledGuard } from 'src/app/shared/guard/integrations.guards';
import { onSiteProtectedGuard } from 'src/app/shared/guard/on-site.guards';
import { permissionGuard } from 'src/app/shared/guard/permissions.guards';
import {
  determineActivitiesRedirect,
  determineHostOrgGroupsRedirect,
  determineRequestableAdditionsRedirect
} from 'src/app/shared/guard/public-event.guards';
import {
  clearMeetingsStore,
  closeChatOnLeave
} from 'src/app/shared/guard/reporting.guards';
import { createEventTemplateResolver } from 'src/app/shared/resolvers/configure-event-template.resolver';
import { eventTypeFieldSettingsResolver } from 'src/app/shared/resolvers/event-type-field-settings.resolver';
import {
  clearEventUpdates,
  createEventResolver
} from 'src/app/shared/resolvers/manage-event.resolver';
import { onSiteInitializationResolver } from 'src/app/shared/resolvers/on-site.resolver';
import { sectorResolver } from 'src/app/shared/resolvers/sector.resolver';
import { tableViewResolver } from 'src/app/shared/resolvers/table-view.resolver';
import { tagResolver } from 'src/app/shared/resolvers/tag.resolver';
import { PRESENTATION_EVENT_CONTEXT_MODE_ROUTE_DATA_KEY } from 'src/app/shared/services/events/presentations/presentation-event-context.models';
import { ReportingTabRoute } from 'src/app/shared/services/reporting/reporting.models';
import { environment } from 'src/environments/environment';

interface CRMTabRouteData {
  tabKey: CRMTabRoute;
}

interface EmailTabRouteData {
  tabKey: EmailTabRoute;
}

interface EmailOneTimeTabRouteData {
  tabKey: EmailOneTimeTabRoute;
}

interface EmailRecurringTabRouteData {
  tabKey: EmailRecurringTabRoute;
}

interface ConfigureEventRouteData {
  tabKey: ConfigureEventTabRoute;
}

interface EmailNotificationRouteData {
  tabKey: EmailNotificationTabRoute;
}

interface EventDetailsRouteData {
  tabKey: EventDetailsTabRoute;
}

interface ScheduleAdditionsRouteData {
  tabKey?: ScheduleAdditionsTab;
  viewingRequests?: boolean;
}

interface IntelligenceHubRouteData {
  tabKey: IntelligenceHubTabRoute;
}

interface IntelligenceHubScorecardRouteData {
  tabKey?: IntelligenceHubScorecardTab;
  fullScreen?: boolean;
}

interface ReportingRouteData {
  tabKey: ReportingTabRoute;
}

interface AdminRouteData {
  tabKey: AdminTabRoute;
}

interface AdminEmailsRouteData {
  tabKey: AdminEmailsTab;
}

interface TeamsAndGroupsRouteData {
  tabKey: TeamsAndGroupsTabRoute;
}

const hidePortalBannerRouteData = {
  hidePortalBanner: true,
  hidePortalFooter: true
};

const publicStepControlsRouteData = {
  ...hidePortalBannerRouteData,
  showPublicStepControls: true
};

const publicRegistrationUpdateStepRouteData = {
  ...publicStepControlsRouteData,
  mode: 'update'
};

const redirectWithParams =
  (redirectPath: string): RedirectFunction =>
  ({ params = {}, queryParams = {} }) => {
    try {
      const resolvedPath = Object.entries(params).reduce(
        (path, [key, value]) =>
          path.replace(`:${key}`, encodeURIComponent(value ?? '')),
        redirectPath
      );

      return Object.keys(queryParams).length
        ? `${resolvedPath}?${new HttpParams({ fromObject: queryParams }).toString()}`
        : resolvedPath;
    } catch {
      return redirectPath;
    }
  };

const emailRoutes = (canActivate: CanActivateFn[]): Route => ({
  path: 'email',
  loadComponent: () =>
    import('./app/features/email/container/axle-email-container.component').then(
      (c) => c.AxleEmailContainerComponent
    ),
  canActivate,
  canDeactivate: [clearEmailStore],
  title: 'Email',
  data: { tabKey: 'email' } as EventDetailsRouteData,
  children: [
    {
      path: 'one-time',
      loadComponent: () =>
        import('src/app/features/email/one-time/container/axle-email-one-time-container.component').then(
          (c) => c.AxleEmailOneTimeContainerComponent
        ),
      title: 'One-Time Emails',
      data: { tabKey: 'one-time' } as EmailTabRouteData,
      children: [
        {
          path: 'drafts',
          loadComponent: () =>
            import('src/app/features/email/one-time/drafts/axle-email-one-time-drafts.component').then(
              (c) => c.AxleEmailOneTimeDraftsComponent
            ),
          title: 'Draft Emails',
          data: { tabKey: 'drafts' } as EmailOneTimeTabRouteData
        },
        {
          path: 'sent',
          loadComponent: () =>
            import('src/app/features/email/one-time/sent/axle-email-one-time-sent.component').then(
              (c) => c.AxleEmailOneTimeSentComponent
            ),
          title: 'Sent Emails',
          data: { tabKey: 'sent' } as EmailOneTimeTabRouteData
        },
        {
          path: 'scheduled',
          loadComponent: () =>
            import('src/app/features/email/one-time/scheduled/axle-email-one-time-scheduled.component').then(
              (c) => c.AxleEmailOneTimeScheduledComponent
            ),
          title: 'Scheduled Emails',
          data: { tabKey: 'scheduled' } as EmailOneTimeTabRouteData
        },
        {
          path: '**',
          redirectTo: 'drafts'
        }
      ]
    },
    {
      path: 'recurring',
      loadComponent: () =>
        import('src/app/features/email/recurring/container/axle-email-recurring-container.component').then(
          (c) => c.AxleEmailRecurringContainerComponent
        ),
      title: 'Recurring Emails',
      data: { tabKey: 'recurring' } as EmailTabRouteData,
      children: [
        {
          path: 'live',
          loadComponent: () =>
            import('src/app/features/email/recurring/live/axle-email-recurring-live.component').then(
              (c) => c.AxleEmailRecurringLiveComponent
            ),
          title: 'Live Recurring Emails',
          data: { tabKey: 'live' } as EmailRecurringTabRouteData
        },
        {
          path: 'draft',
          loadComponent: () =>
            import('src/app/features/email/recurring/draft/axle-email-recurring-draft.component').then(
              (c) => c.AxleEmailRecurringDraftComponent
            ),
          title: 'Draft Recurring Emails',
          data: { tabKey: 'draft' } as EmailRecurringTabRouteData
        },
        {
          path: 'deactivated',
          loadComponent: () =>
            import('src/app/features/email/recurring/deactivated/axle-email-recurring-deactivated.component').then(
              (c) => c.AxleEmailRecurringDeactivatedComponent
            ),
          title: 'Deactivated Recurring Emails',
          data: { tabKey: 'deactivated' } as EmailRecurringTabRouteData
        },
        {
          path: '**',
          redirectTo: 'live'
        }
      ]
    },
    {
      path: '**',
      redirectTo: 'one-time'
    }
  ]
});

export const routes: Routes = [
  {
    path: 'dev/modal-preview',
    loadComponent: () =>
      import('./app/dev/modal-preview/axle-dev-modal-preview.component').then(
        (c) => c.AxleDevModalPreviewComponent
      ),
    // canMatch, not canActivate: where the flag is off the route does not exist,
    // so the URL falls through to the not-found handler instead of a blank outlet.
    canMatch: [() => environment.modalPreview],
    title: 'Modal Preview'
  },
  {
    path: 'auth',
    children: [],
    canActivate: [redirectToAxleAccessDomain, determineAuthRedirectTo],
    title: 'Authentication'
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./app/features/login/axle-login.component').then(
        (c) => c.AxleLoginComponent
      ),
    canActivate: [
      redirectToAxleAccessDomain,
      handleInvitationSignup,
      redirectIfAuthenticated
    ],
    title: 'Login'
  },
  {
    path: 'opt-out',
    loadComponent: () =>
      import('./app/features/unsubscribe/axle-unsubscribe.component').then(
        (c) => c.AxleUnsubscribeComponent
      ),
    title: 'Unsubscribe'
  },
  {
    path: 'password',
    children: [
      {
        path: 'reset',
        loadComponent: () =>
          import('./app/features/reset-password/container/axle-reset-password-container.component').then(
            (c) => c.AxleResetPasswordContainerComponent
          )
      }
    ],
    canActivate: [redirectToAxleAccessDomain],
    title: 'Password Reset'
  },
  {
    path: 'public',
    children: [
      {
        path: 'events-hub/:coordinatingOrgName',
        loadComponent: () =>
          import('./app/features/events-hub/container/axle-events-hub-container.component').then(
            (c) => c.AxleEventsHubContainerComponent
          ),
        title: 'Events Hub',
        children: [
          {
            path: 'login',
            loadComponent: () =>
              import('./app/features/events-hub/login/axle-events-hub-login.component').then(
                (c) => c.AxleEventsHubLoginComponent
              ),
            title: 'Events Hub (Login)'
          },
          {
            path: 'private-link-emailed',
            loadComponent: () =>
              import('./app/features/events-hub/private-link-emailed/axle-events-hub-private-link-emailed.component').then(
                (c) => c.AxleEventsHubPrivateLinkEmailedComponent
              ),
            title: 'Events Hub (Private Link Emailed)'
          },
          {
            path: 'unknown-email',
            loadComponent: () =>
              import('./app/features/events-hub/unknown-email/axle-events-hub-unknown-email.component').then(
                (c) => c.AxleEventsHubUnknownEmailComponent
              ),
            title: 'Events Hub (Unknown Email)'
          },
          {
            path: 'all-events',
            loadComponent: () =>
              import('./app/features/events-hub/all-events-container/axle-events-hub-all-events-container.component').then(
                (c) => c.AxleEventsHubAllEventsContainerComponent
              ),
            title: 'Events Hub (All Events)'
          },
          {
            path: 'not-found',
            loadComponent: () =>
              import('./app/features/events-hub/hubs-not-found/axle-public-events-hub-not-found.component').then(
                (c) => c.AxlePublicEventsHubNotFoundComponent
              ),
            title: 'Events Hub (Not Found)'
          },
          {
            path: '**',
            redirectTo: 'login'
          }
        ]
      },
      {
        path: 'on-site/events',
        loadComponent: () =>
          import('./app/features/on-site/container/axle-public-on-site-container.component').then(
            (c) => c.AxlePublicOnSiteContainerComponent
          ),
        title: 'On-Site Details',
        children: [
          {
            path: ':id',
            resolve: {
              onSiteInitializationResolver: onSiteInitializationResolver
            },
            children: [
              {
                path: 'check-in',
                loadComponent: () =>
                  import('./app/features/on-site/check-in-code/axle-public-on-site-check-in-code.component').then(
                    (c) => c.AxlePublicOnSiteCheckInCodeComponent
                  ),
                title: 'On-Site (Check-in)'
              },
              {
                path: 'forgot-code',
                loadComponent: () =>
                  import('./app/features/on-site/forgot-code/axle-public-on-site-forgot-code.component').then(
                    (c) => c.AxlePublicOnSiteForgotCodeComponent
                  ),
                title: 'On-Site (Check-in)'
              },
              {
                path: 'print-name-badge',
                loadComponent: () =>
                  import('./app/features/on-site/print-name-badge/axle-public-on-site-print-name-badge.component').then(
                    (c) => c.AxlePublicOnSitePrintNameBadgeComponent
                  ),
                title: 'On-Site (Print Name Badge)',
                canActivate: [onSiteProtectedGuard]
              },
              {
                path: 'print-meeting-schedule',
                loadComponent: () =>
                  import('./app/features/on-site/print-meeting-schedule/axle-public-on-site-print-meeting-schedule.component').then(
                    (c) => c.AxlePublicOnSitePrintMeetingScheduleComponent
                  ),
                title: 'On-Site (Print Meeting Schedule)',
                canActivate: [onSiteProtectedGuard]
              },
              {
                path: 'print-presentation-schedule',
                loadComponent: () =>
                  import('./app/features/on-site/print-presentation-schedule/axle-public-on-site-print-presentation-schedule.component').then(
                    (c) => c.AxlePublicOnSitePrintPresentationScheduleComponent
                  ),
                title: 'On-Site (Print Presentation Schedule)',
                canActivate: [onSiteProtectedGuard]
              },
              {
                path: 'check-in-success',
                loadComponent: () =>
                  import('./app/features/on-site/check-in-success/axle-public-on-site-check-in-success.component').then(
                    (c) => c.AxlePublicOnSiteCheckInSuccessComponent
                  ),
                title: 'On-Site (Check-in Success)',
                canActivate: [onSiteProtectedGuard]
              },
              {
                path: '**',
                redirectTo: 'check-in'
              }
            ]
          }
        ]
      },
      {
        path: 'events',
        children: [
          {
            path: 'corporate/:id',
            redirectTo: redirectWithParams('/public/events/:id')
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./app/features/public/container/axle-public-container.component').then(
                (c) => c.AxlePublicContainerComponent
              ),
            title: 'Event Details',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./app/features/public/overview/axle-public-overview.component').then(
                    (c) => c.AxlePublicOverviewComponent
                  )
              },
              {
                path: 'additionalInfo',
                loadComponent: () =>
                  import('./app/features/public/additional-info/registration/axle-public-registration-additional-info.component').then(
                    (c) => c.AxlePublicAdditionalInfoRegistrationComponent
                  ),
                title: 'Additional Information',
                data: hidePortalBannerRouteData
              },
              {
                path: 'alreadyRegistered',
                loadComponent: () =>
                  import('./app/features/public/already-registered/axle-public-already-registered.component').then(
                    (c) => c.AxlePublicAlreadyRegisteredComponent
                  ),
                title: "You're Already Registered",
                data: hidePortalBannerRouteData
              },
              {
                path: 'alreadyRegisteredHostRsvp',
                loadComponent: () =>
                  import('./app/features/public/already-registered-host-rsvp/axle-public-already-registered-host-rsvp.component').then(
                    (c) => c.AxlePublicAlreadyRegisteredHostRsvpComponent
                  ),
                title: "You're Already Registered",
                data: hidePortalBannerRouteData
              },
              {
                path: 'registration',
                title: 'Registration',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./app/features/public/registration/axle-public-registration-container.component').then(
                        (c) => c.AxlePublicRegistrationContainerComponent
                      ),
                    children: [
                      {
                        path: '',
                        loadComponent: () =>
                          import('./app/features/public/registration/registration-table/axle-public-registration-table.component').then(
                            (c) => c.AxlePublicRegistrationTableComponent
                          ),
                        title: 'All Attendees'
                      }
                    ]
                  }
                ]
              },
              {
                path: 'availability',
                loadComponent: () =>
                  import('./app/features/public/availability/axle-public-availability.component').then(
                    (c) => c.AxlePublicAvailabilityComponent
                  ),
                title: 'Add Availability',
                data: publicStepControlsRouteData
              },
              {
                path: 'updateAvailability',
                loadComponent: () =>
                  import('./app/features/public/availability/axle-public-availability.component').then(
                    (c) => c.AxlePublicAvailabilityComponent
                  ),
                title: 'Update Availability',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'corporateRegistration',
                loadComponent: () =>
                  import('./app/features/public/corporate-registration/axle-public-corporate-registration.component').then(
                    (c) => c.AxlePublicCorporateRegistrationComponent
                  ),
                title: 'Registration',
                data: publicStepControlsRouteData
              },
              {
                path: 'segment-screener',
                loadComponent: () =>
                  import('./app/features/public/segment-screener/axle-public-segment-screener.component').then(
                    (c) => c.AxlePublicSegmentScreenerComponent
                  ),
                title: 'Choose a Host type',
                data: publicStepControlsRouteData
              },
              {
                path: 'guest-segment-screener',
                loadComponent: () =>
                  import('./app/features/public/segment-screener/axle-public-segment-screener.component').then(
                    (c) => c.AxlePublicSegmentScreenerComponent
                  ),
                title: 'Choose a Guest type',
                data: {
                  ...publicStepControlsRouteData,
                  audience: 'GUEST'
                }
              },
              {
                path: 'eventNotFound',
                loadComponent: () =>
                  import('./app/features/public/event-not-found/axle-public-event-not-found.component').then(
                    (c) => c.AxlePublicEventNotFoundComponent
                  ),
                title: 'Event Not Found'
              },
              {
                path: 'hosts',
                loadComponent: () =>
                  import('./app/features/public/hosts/axle-public-hosts-container.component').then(
                    (c) => c.AxlePublicHostsContainerComponent
                  ),
                title: 'All Hosts'
              },
              {
                path: 'requests',
                title: 'Requests',
                children: [
                  {
                    path: 'host',
                    loadComponent: () =>
                      import('./app/features/public/requests/corporate/axle-corporate-requests-container.component').then(
                        (c) => c.AxleCorporateRequestsContainerComponent
                      ),
                    children: [
                      {
                        path: '',
                        loadComponent: () =>
                          import('./app/features/public/requests/corporate/view-requests/axle-view-requests.component').then(
                            (c) => c.AxleViewRequestsComponent
                          ),
                        title: 'All Requests'
                      },
                      {
                        path: 'approve',
                        loadComponent: () =>
                          import('./app/features/public/requests/corporate/approve-schedules/axle-approve-schedules.component').then(
                            (c) => c.AxleApproveSchedulesComponent
                          ),
                        title: 'Approve Requests'
                      }
                    ]
                  },
                  {
                    path: 'guest',
                    loadComponent: () =>
                      import('./app/features/public/requests/guest/requests-table/axle-public-requests-table.component').then(
                        (c) => c.AxlePublicRequestsTableComponent
                      ),
                    title: 'All Requests'
                  }
                ]
              },
              {
                path: 'request-preferences',
                loadComponent: () =>
                  import('./app/features/public/request-preferences/axle-request-preferences.component').then(
                    (c) => c.AxleRequestPreferencesComponent
                  ),
                title: 'Request Preferences',
                data: publicStepControlsRouteData
              },
              {
                path: 'host-org-groups',
                loadComponent: () =>
                  import('./app/features/public/host-org-groups/axle-public-host-org-groups.component').then(
                    (c) => c.AxlePublicHostOrgGroupsComponent
                  ),
                title: 'Select Host Organizations',
                canActivate: [determineHostOrgGroupsRedirect],
                data: publicStepControlsRouteData
              },
              {
                path: 'update-host-org-groups',
                loadComponent: () =>
                  import('./app/features/public/host-org-groups/axle-public-host-org-groups.component').then(
                    (c) => c.AxlePublicHostOrgGroupsComponent
                  ),
                title: 'Update Host Organizations',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'update-request-preferences',
                loadComponent: () =>
                  import('./app/features/public/request-preferences/axle-request-preferences.component').then(
                    (c) => c.AxleRequestPreferencesComponent
                  ),
                title: 'Update Request Preferences',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'schedule',
                loadComponent: () =>
                  import('./app/features/public/schedule/axle-public-schedule.component').then(
                    (c) => c.AxlePublicScheduleComponent
                  ),
                title: 'Meeting Schedules'
              },
              {
                path: 'participation',
                loadComponent: () =>
                  import('./app/features/public/corporate-participation/axle-public-participation.component').then(
                    (c) => c.AxlePublicParticipationComponent
                  ),
                title: 'Your Participation',
                data: publicStepControlsRouteData
              },
              {
                path: 'presentations',
                loadComponent: () =>
                  import('./app/features/public/presentations/container/axle-public-presentations-container.component').then(
                    (c) => c.AxlePublicPresentationsContainerComponent
                  ),
                title: 'Presentations'
              },
              {
                path: 'presentation-participation',
                loadComponent: () =>
                  import('./app/features/public/presentation-participation/axle-public-presentation-participation.component').then(
                    (c) => c.AxlePublicPresentationParticipationComponent
                  ),
                title: 'Presentation Participation',
                data: publicStepControlsRouteData
              },
              {
                path: 'update-presentation-participation',
                loadComponent: () =>
                  import('./app/features/public/presentation-participation/axle-public-presentation-participation.component').then(
                    (c) => c.AxlePublicPresentationParticipationComponent
                  ),
                title: 'Update Presentation Participation',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'questionnaire',
                loadComponent: () =>
                  import('./app/features/public/questionnaire/axle-public-questionnaire.component').then(
                    (c) => c.AxlePublicQuestionnaireComponent
                  ),
                title: 'Questionnaire',
                data: publicStepControlsRouteData
              },
              {
                path: 'select-contact',
                loadComponent: () =>
                  import('./app/features/public/select-contact/axle-public-select-contact.component').then(
                    (c) => c.AxlePublicSelectContactComponent
                  ),
                title: 'Select Your Contact',
                data: publicStepControlsRouteData
              },
              {
                path: 'rsvp',
                children: [
                  {
                    path: 'select-contact',
                    loadComponent: () =>
                      import('./app/features/public/select-contact/axle-public-select-contact.component').then(
                        (c) => c.AxlePublicSelectContactComponent
                      ),
                    title: 'Select Your Contact',
                    data: {
                      ...publicStepControlsRouteData,
                      flow: 'rsvp'
                    }
                  },
                  {
                    path: 'questionnaire',
                    children: [
                      {
                        path: '',
                        loadComponent: () =>
                          import('./app/features/public/questionnaire/rsvp/axle-rsvp-questionnaire.component').then(
                            (c) => c.AxleRsvpQuestionnaireComponent
                          ),
                        title: 'Questionnaire',
                        data: publicStepControlsRouteData
                      },
                      {
                        path: 'update',
                        loadComponent: () =>
                          import('./app/features/public/questionnaire/rsvp/axle-rsvp-questionnaire.component').then(
                            (c) => c.AxleRsvpQuestionnaireComponent
                          ),
                        title: 'Update RSVP',
                        data: publicRegistrationUpdateStepRouteData
                      }
                    ]
                  },
                  {
                    path: 'additionalInfo',
                    loadComponent: () =>
                      import('./app/features/public/additional-info/rsvp/axle-public-rsvp-additional-info.component').then(
                        (c) => c.AxlePublicAdditionalInfoRsvpComponent
                      ),
                    title: 'Additional Information'
                  },
                  {
                    path: 'presentation-participation',
                    children: [
                      {
                        path: '',
                        loadComponent: () =>
                          import('./app/features/public/presentation-participation/axle-public-presentation-participation.component').then(
                            (c) =>
                              c.AxlePublicPresentationParticipationComponent
                          ),
                        title: 'Presentation Participation',
                        data: {
                          ...publicStepControlsRouteData,
                          flow: 'rsvp'
                        }
                      },
                      {
                        path: 'update',
                        loadComponent: () =>
                          import('./app/features/public/presentation-participation/axle-public-presentation-participation.component').then(
                            (c) =>
                              c.AxlePublicPresentationParticipationComponent
                          ),
                        title: 'Update Presentation Participation',
                        data: {
                          ...publicRegistrationUpdateStepRouteData,
                          flow: 'rsvp'
                        }
                      }
                    ]
                  },
                  {
                    path: 'success',
                    loadComponent: () =>
                      import('./app/features/public/rsvp-success/axle-rsvp-success.component').then(
                        (c) => c.AxleRsvpSuccessComponent
                      ),
                    title: 'RSVP Successful'
                  },
                  {
                    path: 'waitlisted',
                    loadComponent: () =>
                      import('./app/features/public/rsvp-waitlisted/axle-rsvp-waitlisted.component').then(
                        (c) => c.AxleRsvpWaitlistedComponent
                      ),
                    title: 'RSVP Waitlisted'
                  }
                ],
                title: 'RSVP Event',
                data: hidePortalBannerRouteData
              },
              {
                path: 'updateQuestionnaire',
                loadComponent: () =>
                  import('./app/features/public/questionnaire/axle-public-questionnaire.component').then(
                    (c) => c.AxlePublicQuestionnaireComponent
                  ),
                title: 'Update Questionnaire',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'registerColleague',
                loadComponent: () =>
                  import('./app/features/public/register-colleague/axle-register-colleague.component').then(
                    (c) => c.AxleRegisterColleagueComponent
                  ),
                title: 'Register a Colleague',
                data: publicStepControlsRouteData
              },
              {
                path: 'registerNewColleague',
                loadComponent: () =>
                  import('./app/features/public/register-new-colleague/axle-register-new-colleague.component').then(
                    (c) => c.AxleRegisterNewColleagueComponent
                  ),
                title: 'Register a New Colleague',
                data: publicStepControlsRouteData
              },
              {
                path: 'registration-success',
                loadComponent: () =>
                  import('./app/features/public/registration-success/axle-registration-success.component').then(
                    (c) => c.AxleRegistrationSuccessComponent
                  ),
                title: 'Registration Successful',
                data: hidePortalBannerRouteData
              },
              {
                path: 'meetings-interstitial',
                loadComponent: () =>
                  import('./app/features/public/meetings-interstitial/axle-public-meetings-interstitial.component').then(
                    (c) => c.AxlePublicMeetingsInterstitialComponent
                  ),
                title: 'Meeting Interest',
                data: publicStepControlsRouteData
              },
              {
                path: 'requestedOrgs',
                loadComponent: () =>
                  import('./app/features/public/requested-orgs/axle-public-requested-orgs.component').then(
                    (c) => c.AxlePublicRequestedOrgsComponent
                  ),
                title: 'Requested Organizations',
                data: publicStepControlsRouteData
              },
              {
                path: 'updateRequestedOrgs',
                loadComponent: () =>
                  import('./app/features/public/requested-orgs/axle-public-requested-orgs.component').then(
                    (c) => c.AxlePublicRequestedOrgsComponent
                  ),
                title: 'Update Requested Organizations',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'activities',
                loadComponent: () =>
                  import('./app/features/public/activities/axle-public-activities.component').then(
                    (c) => c.AxlePublicActivitiesComponent
                  ),
                title: 'Activities',
                canActivate: [determineActivitiesRedirect],
                data: publicStepControlsRouteData
              },
              {
                path: 'updateActivities',
                loadComponent: () =>
                  import('./app/features/public/activities/axle-public-activities.component').then(
                    (c) => c.AxlePublicActivitiesComponent
                  ),
                title: 'Update Requests',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'requestable-additions',
                loadComponent: () =>
                  import('./app/features/public/requestable-additions/axle-public-requestable-additions.component').then(
                    (c) => c.AxlePublicRequestableAdditionsComponent
                  ),
                title: 'Schedule Additions',
                canActivate: [determineRequestableAdditionsRedirect],
                data: publicStepControlsRouteData
              },
              {
                path: 'update-requestable-additions',
                loadComponent: () =>
                  import('./app/features/public/requestable-additions/axle-public-requestable-additions.component').then(
                    (c) => c.AxlePublicRequestableAdditionsComponent
                  ),
                title: 'Update Schedule Additions',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'selectMeeting',
                loadComponent: () =>
                  import('./app/features/public/select-meeting-time/axle-public-select-meeting-time.component').then(
                    (c) => c.AxlePublicSelectMeetingTimeComponent
                  ),
                title: 'Select a Meeting',
                data: publicStepControlsRouteData
              },
              {
                path: 'selectMeetings',
                loadComponent: () =>
                  import('./app/features/public/select-meetings/axle-public-select-meetings.component').then(
                    (c) => c.AxlePublicSelectMeetingsComponent
                  ),
                title: 'Select Meetings',
                data: publicStepControlsRouteData
              },
              {
                path: 'updateSelectMeetings',
                loadComponent: () =>
                  import('./app/features/public/select-meetings/axle-public-select-meetings.component').then(
                    (c) => c.AxlePublicSelectMeetingsComponent
                  ),
                title: 'Update Selected Meetings',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'updateSelectMeeting',
                loadComponent: () =>
                  import('./app/features/public/select-meeting-time/axle-public-select-meeting-time.component').then(
                    (c) => c.AxlePublicSelectMeetingTimeComponent
                  ),
                title: 'Update Selected Meeting',
                data: publicRegistrationUpdateStepRouteData
              },
              {
                path: 'feedback',
                title: 'Feedback Reports',
                children: [
                  {
                    path: 'guest',
                    loadComponent: () =>
                      import('./app/features/public/post-event-feedback/guest-container/axle-public-post-event-guest-feedback-container.component').then(
                        (c) =>
                          c.AxlePublicPostEventGuestFeedbackContainerComponent
                      ),
                    title: 'Feedback Reports (Guest)'
                  },
                  {
                    path: 'host',
                    loadComponent: () =>
                      import('./app/features/public/post-event-feedback/host-container/axle-public-post-event-host-feedback-container.component').then(
                        (c) =>
                          c.AxlePublicPostEventHostFeedbackContainerComponent
                      ),
                    title: 'Feedback Reports (Host)'
                  }
                ]
              },
              {
                path: 'sitemap',
                loadComponent: () =>
                  import('./app/features/public/sitemap/axle-public-sitemap.component').then(
                    (c) => c.AxlePublicSitemapComponent
                  ),
                title: 'Site Map'
              },
              {
                path: ':additionalTabId',
                loadComponent: () =>
                  import('./app/features/public/additional-tab/axle-public-additional-tab.component').then(
                    (c) => c.AxlePublicAdditionalTabComponent
                  ),
                title: 'Additional Tab'
              }
            ]
          }
        ],
        title: 'Your Event'
      }
    ]
  },
  {
    path: '',
    loadComponent: () =>
      import('./app/shared/components/containers/auth-container/axle-auth-container.component').then(
        (c) => c.AxleAuthContainerComponent
      ),
    resolve: {
      tags: tagResolver,
      sectors: sectorResolver,
      tableViews: tableViewResolver,
      eventTypeFieldSettings: eventTypeFieldSettingsResolver
    },
    canActivate: [parentAuthGuardFn, redirectToAxleAccessDomain],
    children: [
      {
        path: 'admin-v777rpNsg_5hjfsdkhjsdfkhjsdfkjhsdfmuegcko6F4w',
        canActivate: [permissionGuard(({ isAdmin }) => isAdmin())],
        loadComponent: () =>
          import('./app/features/admin/container/axle-admin-container.component').then(
            (c) => c.AxleAdminContainerComponent
          ),
        title: 'Admin Portal',
        children: [
          {
            path: 'user-management',
            canActivate: [
              permissionGuard(
                ({ canViewUsers }) => canViewUsers(),
                ({ isIntegrationsAdmin }) =>
                  isIntegrationsAdmin()
                    ? '/admin-v777rpNsg_5hjfsdkhjsdfkhjsdfkjhsdfmuegcko6F4w/integrations'
                    : '/crm/contacts'
              )
            ],
            data: { tabKey: 'user-management' } as AdminRouteData,
            children: [
              {
                path: 'users',
                loadComponent: () =>
                  import('./app/features/admin/user-management/users/axle-admin-users.component').then(
                    (c) => c.AxleAdminUsersComponent
                  ),
                title: 'Users'
              },
              {
                path: 'teams-and-groups',
                loadComponent: () =>
                  import('./app/features/admin/user-management/teams-and-groups/axle-admin-teams-and-groups.component').then(
                    (c) => c.AxleAdminTeamsAndGroupsComponent
                  ),
                children: [
                  {
                    path: 'teams',
                    loadComponent: () =>
                      import('./app/features/admin/user-management/teams-and-groups/teams/axle-admin-teams.component').then(
                        (c) => c.AxleAdminTeamsComponent
                      ),
                    data: { tabKey: 'teams' } as TeamsAndGroupsRouteData,
                    title: 'Teams'
                  },
                  {
                    path: 'release-groups',
                    loadComponent: () =>
                      import('./app/features/admin/user-management/teams-and-groups/release-groups/axle-admin-release-groups.component').then(
                        (c) => c.AxleAdminReleaseGroupsComponent
                      ),
                    data: {
                      tabKey: 'release-groups'
                    } as TeamsAndGroupsRouteData,
                    title: 'Groups'
                  },
                  {
                    path: '**',
                    redirectTo: 'teams'
                  }
                ]
              },
              {
                path: '**',
                redirectTo: 'users'
              }
            ]
          },
          {
            path: 'permissions',
            canActivate: [
              permissionGuard(({ canManageUsers }) => canManageUsers())
            ],
            data: { tabKey: 'permissions' } as AdminRouteData,
            children: [
              {
                path: 'feature-roles',
                loadComponent: () =>
                  import('./app/features/admin/permissions/feature-roles/axle-admin-feature-roles.component').then(
                    (c) => c.AxleAdminFeatureRolesComponent
                  ),
                title: 'Feature Roles'
              },
              {
                path: 'data-visibility',
                loadComponent: () =>
                  import('./app/features/admin/permissions/data-roles/axle-admin-data-roles.component').then(
                    (c) => c.AxleAdminDataRolesComponent
                  ),
                title: 'Data Roles'
              },
              {
                path: '**',
                redirectTo: 'feature-roles'
              }
            ]
          },
          {
            path: 'integrations',
            canActivate: [
              permissionGuard(({ isIntegrationsAdmin }) =>
                isIntegrationsAdmin()
              )
            ],
            loadComponent: () =>
              import('./app/features/admin/integrations/axle-admin-integrations.component').then(
                (c) => c.AxleAdminIntegrationsComponent
              ),
            title: 'Integrations',
            data: { tabKey: 'integrations' } as AdminRouteData
          },
          {
            path: 'emails',
            canActivate: [
              permissionGuard(({ canManageUsers }) => canManageUsers())
            ],
            loadComponent: () =>
              import('./app/features/admin/emails/axle-admin-emails.component').then(
                (c) => c.AxleAdminEmailsComponent
              ),
            data: { tabKey: 'emails' } as AdminRouteData,
            children: [
              {
                path: 'domains',
                loadComponent: () =>
                  import('./app/features/admin/emails/domains/axle-admin-emails-domains.component').then(
                    (c) => c.AxleAdminEmailsDomainsComponent
                  ),
                title: 'Emails - Domains',
                data: { tabKey: 'domains' } as AdminEmailsRouteData
              },
              {
                path: 'notifications',
                loadComponent: () =>
                  import('./app/features/admin/emails/notifications/axle-admin-emails-notifications.component').then(
                    (c) => c.AxleAdminEmailsNotificationsComponent
                  ),
                title: 'Emails - Notifications',
                data: { tabKey: 'notifications' } as AdminEmailsRouteData
              },
              {
                path: 'outbound',
                loadComponent: () =>
                  import('./app/features/admin/emails/outbound/axle-admin-emails-outbound.component').then(
                    (c) => c.AxleAdminEmailsOutboundComponent
                  ),
                title: 'Emails - Outbound',
                data: { tabKey: 'outbound' } as AdminEmailsRouteData
              },
              {
                path: '**',
                redirectTo: 'outbound'
              }
            ]
          },
          {
            path: '**',
            redirectTo: 'user-management/users'
          }
        ]
      },
      {
        path: 'home',
        title: 'Home',
        canActivate: [permissionGuard(({ canViewEvents }) => canViewEvents())],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./app/features/home/primary-container/axle-home-primary-container.component').then(
                (c) => c.AxleHomePrimaryContainerComponent
              ),
            title: 'Home'
          }
        ]
      },
      {
        path: 'crm',
        loadComponent: () =>
          import('./app/features/crm/container/axle-crm-container.component').then(
            (c) => c.AxleCRMContainerComponent
          ),
        canDeactivate: [clearContactsStore],
        title: 'CRM',
        children: [
          {
            path: 'contacts',
            loadComponent: () =>
              import('./app/features/crm/user/container/axle-user-contacts-container.component').then(
                (c) => c.AxleUserContactsContainerComponent
              ),
            title: 'Contacts',
            data: { tabKey: 'contacts' } as CRMTabRouteData
          },
          {
            path: 'organizations',
            loadComponent: () =>
              import('./app/features/crm/org/container/axle-org-contacts-container.component').then(
                (c) => c.AxleOrgContactsContainerComponent
              ),
            title: 'Organizations',
            data: { tabKey: 'organizations' } as CRMTabRouteData
          },
          {
            path: 'colleagues',
            loadComponent: () =>
              import('./app/features/crm/user/container/axle-user-contacts-container.component').then(
                (c) => c.AxleUserContactsContainerComponent
              ),
            title: 'My Organization',
            data: { tabKey: 'colleagues' } as CRMTabRouteData
          },
          {
            path: 'tags',
            canActivate: [
              permissionGuard(({ canManageTags }) => canManageTags())
            ],
            loadComponent: () =>
              import('./app/features/crm/tags/axle-crm-tags-container.component').then(
                (c) => c.AxleCRMTagsContainerComponent
              ),
            title: 'Tags',
            data: { tabKey: 'tags' } as CRMTabRouteData
          },
          {
            path: 'audit-log',
            canActivate: [
              permissionGuard(({ canViewAuditLog }) => canViewAuditLog())
            ],
            loadComponent: () =>
              import('./app/features/crm/audit-log/axle-crm-audit-log-container.component').then(
                (c) => c.AxleCRMAuditLogContainerComponent
              ),
            title: 'Audit log',
            data: { tabKey: 'audit-log' } as CRMTabRouteData
          },
          {
            path: '**',
            redirectTo: 'contacts'
          }
        ]
      },
      emailRoutes([
        permissionGuard(({ canManageEmails }) => canManageEmails())
      ]),
      {
        path: 'events',
        title: 'Events',
        canActivate: [permissionGuard(({ canViewEvents }) => canViewEvents())],
        canDeactivate: [confirmUpdateEventScheduleModeUnsavedChangesGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./app/features/events/home/events-table-container/axle-events-table-container.component').then(
                (c) => c.AxleEventsTableContainerComponent
              ),
            title: 'All Events'
          },
          {
            path: 'templates',
            title: 'Event Templates',
            canActivate: [
              permissionGuard(
                ({ canCoordinateScheduleEvents, isAllocationCoordinator }) =>
                  canCoordinateScheduleEvents() || isAllocationCoordinator()
              )
            ],
            children: [
              {
                path: 'create',
                loadComponent: () =>
                  import('./app/features/events/templates/manage/container/axle-event-templates-manage-container.component').then(
                    (c) => c.AxleEventTemplatesManageContainerComponent
                  ),
                title: 'Create Event Template',
                resolve: {
                  createEventTemplateResolver: createEventTemplateResolver
                },
                children: [
                  {
                    path: 'information',
                    loadComponent: () =>
                      import('./app/features/events/configure/information/axle-event-configure-information.component').then(
                        (c) => c.AxleEventConfigureInformationComponent
                      ),
                    title: 'Create Event Template (Information)',
                    data: { tabKey: 'information' } as ConfigureEventRouteData
                  },
                  {
                    path: 'portals',
                    loadComponent: () =>
                      import('./app/features/events/configure/portal/axle-event-configure-portal.component').then(
                        (c) => c.AxleEventConfigurePortalComponent
                      ),
                    title: 'Create Event Template (Portals)',
                    data: {
                      tabKey: 'portals'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: 'portalConfiguration',
                    pathMatch: 'full',
                    redirectTo: 'portals'
                  },
                  {
                    path: 'portalSettings',
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: '../portals'
                      },
                      {
                        path: '**',
                        redirectTo: '../portals'
                      }
                    ]
                  },
                  {
                    path: 'calendarInvites',
                    canActivate: [integrationsEnabledGuard],
                    loadComponent: () =>
                      import('./app/features/events/configure/calendar-invites/container/axle-event-configure-calendar-invites.component').then(
                        (c) => c.AxleEventConfigureCalendarInvitesComponent
                      ),
                    title: 'Create Event Template (Calendar Invites)',
                    data: {
                      tabKey: 'calendarInvites'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: 'guests',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/guests/axle-update-event-schedule-mode-calendar-invites-guests.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesGuestsComponent
                          ),
                        title:
                          'Create Event Template (Calendar Invites) - Guests'
                      },
                      {
                        path: 'hosts',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/hosts/axle-update-event-schedule-mode-calendar-invites-hosts.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesHostsComponent
                          ),
                        title:
                          'Create Event Template (Calendar Invites) - Hosts'
                      },
                      {
                        path: 'colleagues',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/colleagues/axle-update-event-schedule-mode-calendar-invites-colleagues.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesColleaguesComponent
                          ),
                        title:
                          'Create Event Template (Calendar Invites) - Colleagues'
                      },
                      {
                        path: '**',
                        redirectTo: 'guests'
                      }
                    ]
                  },
                  {
                    path: 'notificationEmails',
                    loadComponent: () =>
                      import('./app/features/events/configure/notification-emails/axle-event-configure-notification-emails.component').then(
                        (c) => c.AxleEventConfigureNotificationEmailsComponent
                      ),
                    title: 'Create Event Template (Notification Emails)',
                    data: {
                      tabKey: 'notificationEmails'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: 'host',
                        loadComponent: () =>
                          import('./app/features/email/notifications/host/axle-email-notifications-host-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsHostContainerComponent
                          ),
                        title: 'Email Notifications (Host)',
                        data: { tabKey: 'host' } as EmailNotificationRouteData
                      },
                      {
                        path: 'guest',
                        loadComponent: () =>
                          import('./app/features/email/notifications/guest/axle-email-notifications-guest-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsGuestContainerComponent
                          ),
                        title: 'Email Notifications (Guest)',
                        data: { tabKey: 'guest' } as EmailNotificationRouteData
                      },
                      {
                        path: 'coordinator',
                        loadComponent: () =>
                          import('./app/features/email/notifications/coordinator/axle-email-notifications-coordinator-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsCoordinatorContainerComponent
                          ),
                        title: 'Email Notifications (Coordinator)',
                        data: {
                          tabKey: 'coordinator'
                        } as EmailNotificationRouteData
                      },
                      {
                        path: '**',
                        redirectTo: 'host'
                      }
                    ]
                  },
                  {
                    path: 'schedules',
                    loadComponent: () =>
                      import('./app/features/events/configure/schedules/axle-event-configure-schedules.component').then(
                        (c) => c.AxleEventConfigureSchedulesComponent
                      ),
                    title: 'Create Event Template (Schedules)',
                    data: {
                      tabKey: 'schedules'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: 'questionnaires',
                    data: {
                      tabKey: 'questionnaires'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: '',
                        loadComponent: () =>
                          import('./app/features/events/configure/questionnaires/axle-event-configure-questionnaires.component').then(
                            (c) => c.AxleEventConfigureQuestionnairesComponent
                          ),
                        title: 'Create Event Template (Questionnaires)'
                      },
                      {
                        path: ':questionnaireId/responses',
                        loadComponent: () =>
                          import('./app/features/events/configure/questionnaires/responses/axle-event-configure-questionnaire-responses.component').then(
                            (c) =>
                              c.AxleEventConfigureQuestionnaireResponsesComponent
                          ),
                        title: 'Create Event Template (Questionnaire Responses)'
                      }
                    ]
                  },
                  {
                    path: 'permissions',
                    loadComponent: () =>
                      import('./app/features/events/configure/permissions/axle-event-configure-permissions.component').then(
                        (c) => c.AxleEventConfigurePermissionsComponent
                      ),
                    title: 'Create Event Template (Permissions)',
                    data: {
                      tabKey: 'permissions'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: '**',
                    redirectTo: 'information'
                  }
                ]
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./app/features/events/templates/manage/container/axle-event-templates-manage-container.component').then(
                    (c) => c.AxleEventTemplatesManageContainerComponent
                  ),
                title: 'Manage Event Template',
                canActivate: [ensureEventTemplateLoadedGuard],
                children: [
                  {
                    path: 'information',
                    loadComponent: () =>
                      import('./app/features/events/configure/information/axle-event-configure-information.component').then(
                        (c) => c.AxleEventConfigureInformationComponent
                      ),
                    title: 'Manage Event Template (Information)',
                    data: { tabKey: 'information' } as ConfigureEventRouteData
                  },
                  {
                    path: 'portals',
                    loadComponent: () =>
                      import('./app/features/events/configure/portal/axle-event-configure-portal.component').then(
                        (c) => c.AxleEventConfigurePortalComponent
                      ),
                    title: 'Manage Event Template (Portals)',
                    data: {
                      tabKey: 'portals'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: 'portalConfiguration',
                    pathMatch: 'full',
                    redirectTo: 'portals'
                  },
                  {
                    path: 'portalSettings',
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: '../portals'
                      },
                      {
                        path: '**',
                        redirectTo: '../portals'
                      }
                    ]
                  },
                  {
                    path: 'calendarInvites',
                    canActivate: [integrationsEnabledGuard],
                    loadComponent: () =>
                      import('./app/features/events/configure/calendar-invites/container/axle-event-configure-calendar-invites.component').then(
                        (c) => c.AxleEventConfigureCalendarInvitesComponent
                      ),
                    title: 'Manage Event Template (Calendar Invites)',
                    data: {
                      tabKey: 'calendarInvites'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: 'guests',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/guests/axle-update-event-schedule-mode-calendar-invites-guests.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesGuestsComponent
                          ),
                        title:
                          'Manage Event Template (Calendar Invites) - Guests'
                      },
                      {
                        path: 'hosts',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/hosts/axle-update-event-schedule-mode-calendar-invites-hosts.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesHostsComponent
                          ),
                        title:
                          'Manage Event Template (Calendar Invites) - Hosts'
                      },
                      {
                        path: 'colleagues',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/colleagues/axle-update-event-schedule-mode-calendar-invites-colleagues.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesColleaguesComponent
                          ),
                        title:
                          'Manage Event Template (Calendar Invites) - Colleagues'
                      },
                      {
                        path: '**',
                        redirectTo: 'guests'
                      }
                    ]
                  },
                  {
                    path: 'notificationEmails',
                    loadComponent: () =>
                      import('./app/features/events/configure/notification-emails/axle-event-configure-notification-emails.component').then(
                        (c) => c.AxleEventConfigureNotificationEmailsComponent
                      ),
                    title: 'Update Event Template (Notification Emails)',
                    data: {
                      tabKey: 'notificationEmails'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: 'host',
                        loadComponent: () =>
                          import('./app/features/email/notifications/host/axle-email-notifications-host-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsHostContainerComponent
                          ),
                        title: 'Email Notifications (Host)',
                        data: { tabKey: 'host' } as EmailNotificationRouteData
                      },
                      {
                        path: 'guest',
                        loadComponent: () =>
                          import('./app/features/email/notifications/guest/axle-email-notifications-guest-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsGuestContainerComponent
                          ),
                        title: 'Email Notifications (Guest)',
                        data: { tabKey: 'guest' } as EmailNotificationRouteData
                      },
                      {
                        path: 'coordinator',
                        loadComponent: () =>
                          import('./app/features/email/notifications/coordinator/axle-email-notifications-coordinator-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsCoordinatorContainerComponent
                          ),
                        title: 'Email Notifications (Coordinator)',
                        data: {
                          tabKey: 'coordinator'
                        } as EmailNotificationRouteData
                      },
                      {
                        path: '**',
                        redirectTo: 'host'
                      }
                    ]
                  },
                  {
                    path: 'schedules',
                    loadComponent: () =>
                      import('./app/features/events/configure/schedules/axle-event-configure-schedules.component').then(
                        (c) => c.AxleEventConfigureSchedulesComponent
                      ),
                    title: 'Update Event Template (Schedules)',
                    data: {
                      tabKey: 'schedules'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: 'questionnaires',
                    data: {
                      tabKey: 'questionnaires'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: '',
                        loadComponent: () =>
                          import('./app/features/events/configure/questionnaires/axle-event-configure-questionnaires.component').then(
                            (c) => c.AxleEventConfigureQuestionnairesComponent
                          ),
                        title: 'Update Event Template (Questionnaires)'
                      },
                      {
                        path: ':questionnaireId/responses',
                        loadComponent: () =>
                          import('./app/features/events/configure/questionnaires/responses/axle-event-configure-questionnaire-responses.component').then(
                            (c) =>
                              c.AxleEventConfigureQuestionnaireResponsesComponent
                          ),
                        title: 'Update Event Template (Questionnaire Responses)'
                      }
                    ]
                  },
                  {
                    path: 'permissions',
                    loadComponent: () =>
                      import('./app/features/events/configure/permissions/axle-event-configure-permissions.component').then(
                        (c) => c.AxleEventConfigurePermissionsComponent
                      ),
                    title: 'Update Event Template (Permissions)',
                    data: {
                      tabKey: 'permissions'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: '**',
                    redirectTo: 'information'
                  }
                ]
              },
              {
                path: '',
                loadComponent: () =>
                  import('./app/features/events/templates/list/axle-event-templates-list.component').then(
                    (c) => c.AxleEventTemplatesListComponent
                  ),
                title: 'All Event Templates'
              }
            ]
          },
          {
            path: 'create',
            title: 'Create Event',
            canActivate: [
              permissionGuard(({ isAllocationCoordinator }) =>
                isAllocationCoordinator()
              )
            ],
            resolve: {
              createEventResolver: createEventResolver
            },
            children: [
              {
                path: 'start',
                loadComponent: () =>
                  import('./app/features/events/create/axle-create-event-interstitial/axle-create-event-interstitial.component').then(
                    (c) => c.AxleCreateEventInterstitialComponent
                  ),
                title: 'Create Event (Start)'
              },
              {
                path: '',
                loadComponent: () =>
                  import('./app/features/events/manage/axle-events-manage.component').then(
                    (c) => c.AxleEventsManageComponent
                  ),
                children: [
                  {
                    path: 'information',
                    loadComponent: () =>
                      import('./app/features/events/configure/information/axle-event-configure-information.component').then(
                        (c) => c.AxleEventConfigureInformationComponent
                      ),
                    title: 'Create Event (Information)',
                    data: { tabKey: 'information' } as ConfigureEventRouteData
                  },
                  {
                    path: 'portals',
                    loadComponent: () =>
                      import('./app/features/events/configure/portal/axle-event-configure-portal.component').then(
                        (c) => c.AxleEventConfigurePortalComponent
                      ),
                    title: 'Create Event (Portals)',
                    data: {
                      tabKey: 'portals'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: 'portalConfiguration',
                    pathMatch: 'full',
                    redirectTo: 'portals'
                  },
                  {
                    path: 'portalSettings',
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: '../portals'
                      },
                      {
                        path: '**',
                        redirectTo: '../portals'
                      }
                    ]
                  },
                  {
                    path: 'calendarInvites',
                    canActivate: [integrationsEnabledGuard],
                    loadComponent: () =>
                      import('./app/features/events/configure/calendar-invites/container/axle-event-configure-calendar-invites.component').then(
                        (c) => c.AxleEventConfigureCalendarInvitesComponent
                      ),
                    title: 'Create Event (Calendar Invites)',
                    data: {
                      tabKey: 'calendarInvites'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: 'guests',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/guests/axle-update-event-schedule-mode-calendar-invites-guests.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesGuestsComponent
                          ),
                        title: 'Create Event (Calendar Invites) - Guests'
                      },
                      {
                        path: 'hosts',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/hosts/axle-update-event-schedule-mode-calendar-invites-hosts.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesHostsComponent
                          ),
                        title: 'Create Event (Calendar Invites) - Hosts'
                      },
                      {
                        path: 'colleagues',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/colleagues/axle-update-event-schedule-mode-calendar-invites-colleagues.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesColleaguesComponent
                          ),
                        title: 'Create Event (Calendar Invites) - Colleagues'
                      },
                      {
                        path: '**',
                        redirectTo: 'guests'
                      }
                    ]
                  },
                  {
                    path: 'notificationEmails',
                    loadComponent: () =>
                      import('./app/features/events/configure/notification-emails/axle-event-configure-notification-emails.component').then(
                        (c) => c.AxleEventConfigureNotificationEmailsComponent
                      ),
                    title: 'Create Event (Notification Emails)',
                    data: {
                      tabKey: 'notificationEmails'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: 'host',
                        loadComponent: () =>
                          import('./app/features/email/notifications/host/axle-email-notifications-host-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsHostContainerComponent
                          ),
                        title: 'Email Notifications (Host)',
                        data: { tabKey: 'host' } as EmailNotificationRouteData
                      },
                      {
                        path: 'guest',
                        loadComponent: () =>
                          import('./app/features/email/notifications/guest/axle-email-notifications-guest-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsGuestContainerComponent
                          ),
                        title: 'Email Notifications (Guest)',
                        data: { tabKey: 'guest' } as EmailNotificationRouteData
                      },
                      {
                        path: 'coordinator',
                        loadComponent: () =>
                          import('./app/features/email/notifications/coordinator/axle-email-notifications-coordinator-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsCoordinatorContainerComponent
                          ),
                        title: 'Email Notifications (Coordinator)',
                        data: {
                          tabKey: 'coordinator'
                        } as EmailNotificationRouteData
                      },
                      {
                        path: '**',
                        redirectTo: 'host'
                      }
                    ]
                  },
                  {
                    path: 'schedules',
                    loadComponent: () =>
                      import('./app/features/events/configure/schedules/axle-event-configure-schedules.component').then(
                        (c) => c.AxleEventConfigureSchedulesComponent
                      ),
                    title: 'Create Event (Schedules)',
                    data: {
                      tabKey: 'schedules'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: 'questionnaires',
                    data: {
                      tabKey: 'questionnaires'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: '',
                        loadComponent: () =>
                          import('./app/features/events/configure/questionnaires/axle-event-configure-questionnaires.component').then(
                            (c) => c.AxleEventConfigureQuestionnairesComponent
                          ),
                        title: 'Create Event (Questionnaires)'
                      },
                      {
                        path: ':questionnaireId/responses',
                        loadComponent: () =>
                          import('./app/features/events/configure/questionnaires/responses/axle-event-configure-questionnaire-responses.component').then(
                            (c) =>
                              c.AxleEventConfigureQuestionnaireResponsesComponent
                          ),
                        title: 'Create Event (Questionnaire Responses)'
                      }
                    ]
                  },
                  {
                    path: 'permissions',
                    loadComponent: () =>
                      import('./app/features/events/configure/permissions/axle-event-configure-permissions.component').then(
                        (c) => c.AxleEventConfigurePermissionsComponent
                      ),
                    title: 'Create Event (Permissions)',
                    data: {
                      tabKey: 'permissions'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: '**',
                    redirectTo: 'information'
                  }
                ]
              }
            ]
          },
          {
            path: SCHEDULE_EVENT_ID_ROUTE,
            loadComponent: () =>
              import('./app/features/events/schedule/container/axle-schedule-event-container.component').then(
                (c) => c.AxleScheduleEventContainerComponent
              ),
            title: 'Event Details',
            canActivate: [ensureScheduleEventDetailsLoadedGuard],
            canDeactivate: [clearScheduleEventStore],
            children: [
              {
                path: SCHEDULE_EVENT_SCHEDULER_ROUTE,
                canActivate: [
                  stripUpdateEventScheduleModeModalForNonCoordinatorGuard
                ],
                loadComponent: () =>
                  import('./app/features/events/schedule/scheduler/axle-schedule-event-scheduler.component').then(
                    (c) => c.AxleScheduleEventSchedulerComponent
                  ),
                title: 'Scheduler'
              },
              {
                path: SCHEDULE_EVENT_REPORTING_ROUTE,
                canActivate: [
                  permissionGuard(({ canViewScheduleEventReporting }) =>
                    canViewScheduleEventReporting()
                  )
                ],
                loadComponent: () =>
                  import('./app/features/events/details/reporting/axle-events-reporting-container-component.component').then(
                    (c) => c.AxleEventsReportingContainerComponentComponent
                  ),
                title: 'Reporting'
              },

              {
                path: SCHEDULE_EVENT_ACTIVITY_LOG_ROUTE,
                canActivate: [
                  permissionGuard(({ canViewScheduleActivityLog }) =>
                    canViewScheduleActivityLog()
                  )
                ],
                loadComponent: () =>
                  import('./app/features/events/schedule/activity-log/axle-schedule-event-activity-log.component').then(
                    (c) => c.AxleScheduleEventActivityLogComponent
                  ),
                title: 'Activity Log',
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    redirectTo: SCHEDULE_EVENT_EVENT_CHANGES_ROUTE
                  },
                  {
                    path: SCHEDULE_EVENT_EVENT_CHANGES_ROUTE,
                    loadComponent: () =>
                      import('./app/features/events/activity-log/event-changes/axle-event-changes.component').then(
                        (c) => c.AxleEventChangesActivityLogComponent
                      ),
                    title: 'Activity Log - Event Changes',
                    data: { tabKey: SCHEDULE_EVENT_EVENT_CHANGES_ROUTE }
                  },
                  {
                    path: SCHEDULE_EVENT_REQUEST_UPDATES_ROUTE,
                    loadComponent: () =>
                      import('./app/features/events/activity-log/request-updates/axle-request-updates-container.component').then(
                        (c) => c.AxleRequestUpdatesContainerComponent
                      ),
                    title: 'Activity Log - Request Updates',
                    data: { tabKey: SCHEDULE_EVENT_REQUEST_UPDATES_ROUTE }
                  },
                  {
                    path: '**',
                    redirectTo: SCHEDULE_EVENT_EVENT_CHANGES_ROUTE
                  }
                ]
              },
              {
                path: SCHEDULE_EVENT_PRESENTATIONS_ROUTE,
                canActivate: [canViewScheduleEventPresentationsGuard],
                loadComponent: () =>
                  import('./app/features/events/itinerary/presentations/container/axle-event-presentations.component').then(
                    (c) => c.AxleEventPresentationsComponent
                  ),
                title: 'Presentations',
                data: {
                  [PRESENTATION_EVENT_CONTEXT_MODE_ROUTE_DATA_KEY]: 'SCHEDULE'
                }
              },
              emailRoutes([
                permissionGuard(({ canManageEmails }) => canManageEmails())
              ]),

              {
                path: '**',
                redirectTo: SCHEDULE_EVENT_SCHEDULER_ROUTE
              }
            ]
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./app/features/events/details/container/axle-events-details.component').then(
                (c) => c.AxleEventsDetailsComponent
              ),
            title: 'Event Details',
            canActivate: [ensureEventDetailsLoadedGuard],
            canDeactivate: [clearEventsStore],
            children: [
              {
                path: 'settings',
                canActivate: [
                  permissionGuard(({ isAllocationCoordinator }) =>
                    isAllocationCoordinator()
                  ),
                  syncEventDetailsGuard
                ],
                loadComponent: () =>
                  import('./app/features/events/manage/axle-events-manage.component').then(
                    (c) => c.AxleEventsManageComponent
                  ),
                title: 'Update Event Details',
                data: { tabKey: 'settings' } as EventDetailsRouteData,
                children: [
                  {
                    path: 'information',
                    loadComponent: () =>
                      import('./app/features/events/configure/information/axle-event-configure-information.component').then(
                        (c) => c.AxleEventConfigureInformationComponent
                      ),
                    title: 'Update Event (Information)',
                    data: { tabKey: 'information' } as ConfigureEventRouteData,
                    canDeactivate: [canDeactivateGuard],
                    resolve: { clearEventUpdates }
                  },
                  {
                    path: 'portals',
                    loadComponent: () =>
                      import('./app/features/events/configure/portal/axle-event-configure-portal.component').then(
                        (c) => c.AxleEventConfigurePortalComponent
                      ),
                    title: 'Update Event (Portals)',
                    data: {
                      tabKey: 'portals'
                    } as ConfigureEventRouteData,
                    resolve: { clearEventUpdates }
                  },
                  {
                    path: 'portalConfiguration',
                    pathMatch: 'full',
                    redirectTo: 'portals'
                  },
                  {
                    path: 'portalSettings',
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: '../portals'
                      },
                      {
                        path: '**',
                        redirectTo: '../portals'
                      }
                    ]
                  },
                  {
                    path: 'calendarInvites',
                    canActivate: [integrationsEnabledGuard],
                    loadComponent: () =>
                      import('./app/features/events/configure/calendar-invites/container/axle-event-configure-calendar-invites.component').then(
                        (c) => c.AxleEventConfigureCalendarInvitesComponent
                      ),
                    title: 'Update Event (Calendar Invites)',
                    data: {
                      tabKey: 'calendarInvites'
                    } as ConfigureEventRouteData,
                    resolve: { clearEventUpdates },
                    canDeactivate: [canDeactivateGuard],
                    children: [
                      {
                        path: 'guests',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/guests/axle-update-event-schedule-mode-calendar-invites-guests.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesGuestsComponent
                          ),
                        title: 'Update Event (Calendar Invites) - Guests'
                      },
                      {
                        path: 'hosts',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/hosts/axle-update-event-schedule-mode-calendar-invites-hosts.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesHostsComponent
                          ),
                        title: 'Update Event (Calendar Invites) - Hosts'
                      },
                      {
                        path: 'colleagues',
                        loadComponent: () =>
                          import('./app/shared/components/modals/update-event-schedule-mode-modal/calendar-invites/colleagues/axle-update-event-schedule-mode-calendar-invites-colleagues.component').then(
                            (c) =>
                              c.AxleUpdateEventScheduleModeCalendarInvitesColleaguesComponent
                          ),
                        title: 'Update Event (Calendar Invites) - Colleagues'
                      },
                      {
                        path: '**',
                        redirectTo: 'guests'
                      }
                    ]
                  },
                  {
                    path: 'notificationEmails',
                    loadComponent: () =>
                      import('./app/features/events/configure/notification-emails/axle-event-configure-notification-emails.component').then(
                        (c) => c.AxleEventConfigureNotificationEmailsComponent
                      ),
                    title: 'Update Event (Notification Emails)',
                    data: {
                      tabKey: 'notificationEmails'
                    } as ConfigureEventRouteData,
                    children: [
                      {
                        path: 'host',
                        loadComponent: () =>
                          import('./app/features/email/notifications/host/axle-email-notifications-host-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsHostContainerComponent
                          ),
                        title: 'Update Notifications (Host)',
                        data: { tabKey: 'host' } as EmailNotificationRouteData
                      },
                      {
                        path: 'guest',
                        loadComponent: () =>
                          import('./app/features/email/notifications/guest/axle-email-notifications-guest-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsGuestContainerComponent
                          ),
                        title: 'Email Notifications (Guest)',
                        data: { tabKey: 'guest' } as EmailNotificationRouteData
                      },
                      {
                        path: 'coordinator',
                        loadComponent: () =>
                          import('./app/features/email/notifications/coordinator/axle-email-notifications-coordinator-container.component').then(
                            (c) =>
                              c.AxleEmailNotificationsCoordinatorContainerComponent
                          ),
                        title: 'Email Notifications (Coordinator)',
                        data: {
                          tabKey: 'coordinator'
                        } as EmailNotificationRouteData
                      },
                      {
                        path: '**',
                        redirectTo: 'host'
                      }
                    ]
                  },
                  {
                    path: 'questionnaires',
                    data: {
                      tabKey: 'questionnaires'
                    } as ConfigureEventRouteData,
                    resolve: { clearEventUpdates },
                    children: [
                      {
                        path: '',
                        loadComponent: () =>
                          import('./app/features/events/configure/questionnaires/axle-event-configure-questionnaires.component').then(
                            (c) => c.AxleEventConfigureQuestionnairesComponent
                          ),
                        title: 'Update Event (Questionnaires)'
                      },
                      {
                        path: ':questionnaireId/responses',
                        loadComponent: () =>
                          import('./app/features/events/configure/questionnaires/responses/axle-event-configure-questionnaire-responses.component').then(
                            (c) =>
                              c.AxleEventConfigureQuestionnaireResponsesComponent
                          ),
                        title: 'Update Event (Questionnaire Responses)'
                      }
                    ]
                  },
                  {
                    path: 'permissions',
                    loadComponent: () =>
                      import('./app/features/events/configure/permissions/axle-event-configure-permissions.component').then(
                        (c) => c.AxleEventConfigurePermissionsComponent
                      ),
                    canDeactivate: [canDeactivateGuard],
                    resolve: { clearEventUpdates },
                    title: 'Update Event (Permissions)',
                    data: {
                      tabKey: 'permissions'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: 'schedules',
                    loadComponent: () =>
                      import('./app/features/events/configure/schedules/axle-event-configure-schedules.component').then(
                        (c) => c.AxleEventConfigureSchedulesComponent
                      ),
                    canDeactivate: [canDeactivateGuard],
                    resolve: { clearEventUpdates },
                    title: 'Update Event (Schedules)',
                    data: {
                      tabKey: 'schedules'
                    } as ConfigureEventRouteData
                  },
                  {
                    path: '**',
                    redirectTo: 'information'
                  }
                ]
              },
              {
                path: 'itinerary',
                title: 'Itinerary',
                data: { tabKey: 'itinerary' } as EventDetailsRouteData,
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    redirectTo: 'meetings'
                  },
                  {
                    path: 'meetings',
                    canActivate: [
                      permissionGuard(({ canViewItineraries }) =>
                        canViewItineraries()
                      )
                    ],
                    loadComponent: () =>
                      import('./app/features/events/itinerary/meetings/axle-event-meeting-itinerary.component').then(
                        (c) => c.AxleEventMeetingItineraryComponent
                      ),
                    title: 'Meeting Itinerary',
                    data: { feature: 'MEETING' }
                  },
                  {
                    path: 'activities',
                    canActivate: [
                      permissionGuard(({ canViewActivities }) =>
                        canViewActivities()
                      )
                    ],
                    loadComponent: () =>
                      import('./app/features/events/itinerary/activities/axle-event-activities.component').then(
                        (c) => c.AxleEventActivitiesComponent
                      ),
                    title: 'Activity Itinerary',
                    data: { feature: 'ACTIVITY' }
                  },
                  {
                    path: 'event',
                    canActivate: [
                      permissionGuard(({ canViewItineraries }) =>
                        canViewItineraries()
                      )
                    ],
                    loadComponent: () =>
                      import('./app/features/events/itinerary/event-level/axle-event-level-itinerary.component').then(
                        (c) => c.AxleEventLevelItineraryComponent
                      ),
                    title: 'Event Itinerary',
                    data: { feature: 'EVENT_LEVEL' }
                  },
                  {
                    path: 'presentations',
                    canActivate: [canViewPresentationsGuard],
                    loadComponent: () =>
                      import('./app/features/events/itinerary/presentations/container/axle-event-presentations.component').then(
                        (c) => c.AxleEventPresentationsComponent
                      ),
                    title: 'Presentations',
                    data: {
                      feature: 'PRESENTATIONS',
                      [PRESENTATION_EVENT_CONTEXT_MODE_ROUTE_DATA_KEY]:
                        'ALLOCATION'
                    }
                  },
                  {
                    path: 'rooms',
                    canActivate: [
                      permissionGuard(({ canViewRooms }) => canViewRooms())
                    ],
                    loadComponent: () =>
                      import('./app/features/events/itinerary/rooms/axle-event-rooms.component').then(
                        (c) => c.AxleEventRoomsComponent
                      ),
                    title: 'Rooms',
                    data: { feature: 'ROOMS' }
                  },
                  {
                    path: 'scheduleAdditions',
                    canActivate: [
                      permissionGuard(({ canViewScheduleAdditions }) =>
                        canViewScheduleAdditions()
                      )
                    ],
                    loadComponent: () =>
                      import('./app/features/events/itinerary/schedule-additions/axle-schedule-additions.component').then(
                        (c) => c.AxleScheduleAdditionsComponent
                      ),
                    title: 'Schedule Additions',
                    data: { feature: 'SCHEDULE_ADDITIONS' },
                    children: [
                      {
                        path: 'requestable',
                        canActivate: [supportsRequestableAdditionsGuard],
                        data: {
                          tabKey: 'requestable'
                        } as ScheduleAdditionsRouteData,
                        children: [
                          {
                            path: '',
                            loadComponent: () =>
                              import('./app/features/events/itinerary/schedule-additions/requestable-additions-table/axle-requestable-additions-table.component').then(
                                (c) => c.AxleRequestableAdditionsTableComponent
                              ),
                            title: 'Requestable Additions'
                          },
                          {
                            path: ':requestableAdditionId/requests',
                            loadComponent: () =>
                              import('./app/features/events/itinerary/schedule-additions/requestable-addition-requests/container/axle-requestable-addition-requests-container.component').then(
                                (c) =>
                                  c.AxleRequestableAdditionRequestsContainerComponent
                              ),
                            title: 'Requestable Addition Requests',
                            data: {
                              viewingRequests: true
                            } as ScheduleAdditionsRouteData
                          }
                        ]
                      },
                      {
                        path: 'schedule',
                        loadComponent: () =>
                          import('./app/features/events/itinerary/schedule-additions/schedule-additions-table/axle-schedule-additions-table.component').then(
                            (c) => c.AxleScheduleAdditionsTableComponent
                          ),
                        title: 'Schedule Additions',
                        data: {
                          tabKey: 'schedule'
                        } as ScheduleAdditionsRouteData
                      },
                      {
                        path: '**',
                        redirectTo: 'requestable'
                      }
                    ]
                  }
                ]
              },
              {
                path: 'allocate',
                canActivate: [canViewAllocationManagerGuard],
                loadComponent: () =>
                  import('./app/features/events/allocation-manager/container/axle-allocation-manager.component').then(
                    (c) => c.AxleAllocationManagerComponent
                  ),
                title: 'Allocation Manager',
                data: { tabKey: 'allocate' } as EventDetailsRouteData
              },
              {
                path: SCHEDULE_EVENT_ACTIVITY_LOG_ROUTE,
                canActivate: [
                  permissionGuard(({ canViewAllocationActivityLog }) =>
                    canViewAllocationActivityLog()
                  )
                ],
                title: 'Activity Log',
                data: {
                  tabKey: SCHEDULE_EVENT_ACTIVITY_LOG_ROUTE
                } as EventDetailsRouteData,
                children: [
                  {
                    path: SCHEDULE_EVENT_EVENT_CHANGES_ROUTE,
                    loadComponent: () =>
                      import('./app/features/events/activity-log/event-changes/axle-event-changes.component').then(
                        (c) => c.AxleEventChangesActivityLogComponent
                      ),
                    title: 'Activity Log - Event Changes'
                  },
                  {
                    path: SCHEDULE_EVENT_REQUEST_UPDATES_ROUTE,
                    loadComponent: () =>
                      import('./app/features/events/activity-log/request-updates/axle-request-updates-container.component').then(
                        (c) => c.AxleRequestUpdatesContainerComponent
                      ),
                    title: 'Activity Log - Request Changes'
                  },
                  {
                    path: '**',
                    redirectTo: SCHEDULE_EVENT_EVENT_CHANGES_ROUTE
                  }
                ]
              },
              {
                path: 'hosts',
                canActivate: [
                  permissionGuard(({ canViewAllocationHostsAndGuests }) =>
                    canViewAllocationHostsAndGuests()
                  )
                ],
                data: { tabKey: 'hosts' } as EventDetailsRouteData,
                children: [
                  {
                    path: 'individuals',
                    loadComponent: () =>
                      import('./app/features/events/details/host-management/host-individuals/axle-host-individuals.component').then(
                        (c) => c.AxleHostIndividualsComponent
                      ),
                    title: 'Host List - Individuals'
                  },
                  {
                    path: 'organizations',
                    loadComponent: () =>
                      import('./app/features/events/details/host-management/host-orgs/axle-host-orgs.component').then(
                        (c) => c.AxleHostOrgsComponent
                      ),
                    title: 'Host List - Organizations'
                  },
                  {
                    path: '**',
                    redirectTo: 'individuals'
                  }
                ]
              },
              {
                path: 'guests',
                canActivate: [
                  permissionGuard(({ canViewAllocationHostsAndGuests }) =>
                    canViewAllocationHostsAndGuests()
                  )
                ],
                data: { tabKey: 'guests' } as EventDetailsRouteData,
                children: [
                  {
                    path: 'individuals',
                    loadComponent: () =>
                      import('./app/features/events/details/guest-list/individuals-container/axle-events-guest-list-individuals-container.component').then(
                        (c) =>
                          c.AxleEventsGuestListIndividualsContainerComponent
                      ),
                    title: 'Guest List - Individuals'
                  },
                  {
                    path: 'organizations',
                    loadComponent: () =>
                      import('./app/features/events/details/guest-list/guest-orgs/axle-guest-orgs.component').then(
                        (c) => c.AxleGuestOrgsComponent
                      ),
                    title: 'Guest List - Organizations'
                  },
                  {
                    path: '**',
                    redirectTo: 'individuals'
                  }
                ]
              },
              {
                path: SCHEDULE_EVENT_REPORTING_ROUTE,
                canActivate: [
                  permissionGuard(({ canViewAllocationEventReporting }) =>
                    canViewAllocationEventReporting()
                  )
                ],
                loadComponent: () =>
                  import('./app/features/events/details/reporting/axle-events-reporting-container-component.component').then(
                    (c) => c.AxleEventsReportingContainerComponentComponent
                  ),
                data: {
                  tabKey: SCHEDULE_EVENT_REPORTING_ROUTE
                } as EventDetailsRouteData
              },
              {
                path: 'requests',
                loadComponent: () =>
                  import('./app/features/events/details/requests/axle-events-requests-container-component.component').then(
                    (c) => c.AxleEventsRequestsContainerComponentComponent
                  ),
                data: { tabKey: 'requests' } as EventDetailsRouteData
              },
              {
                path: 'overview',
                loadComponent: () =>
                  import('./app/features/events/details/overview/axle-event-details-overview.component').then(
                    (c) => c.AxleEventDetailsOverviewV2Component
                  ),
                title: 'Overview',
                data: { tabKey: 'overview' } as EventDetailsRouteData
              },
              emailRoutes([
                permissionGuard(({ canManageEmails }) => canManageEmails())
              ]),
              {
                path: '**',
                redirectTo: 'overview'
              }
            ]
          }
        ]
      },
      {
        path: 'intelligence-hub',
        loadComponent: () =>
          import('./app/features/intelligence-hub/container/axle-intelligence-hub-container.component').then(
            (c) => c.AxleIntelligenceHubContainerComponent
          ),
        title: 'Intelligence Hub',
        canActivate: [
          permissionGuard(({ clientIntelligenceEnabled }) =>
            clientIntelligenceEnabled()
          )
        ],
        children: [
          {
            path: 'scorecard',
            loadComponent: () =>
              import('./app/features/intelligence-hub/scorecard/container/axle-intelligence-hub-scorecard-container.component').then(
                (c) => c.AxleIntelligenceHubScorecardContainerComponent
              ),
            title: 'Scorecard',
            data: { tabKey: 'scorecard' } as IntelligenceHubRouteData,
            children: [
              {
                path: 'client-rankings',
                loadComponent: () =>
                  import('src/app/features/intelligence-hub/scorecard/client-rankings/axle-intelligence-hub-scorecard-client-rankings.component').then(
                    (c) => c.AxleIntelligenceHubScorecardClientRankingsComponent
                  ),
                title: 'Client Rankings',
                data: {
                  tabKey: 'client-rankings'
                } as IntelligenceHubScorecardRouteData
              },
              {
                path: 'ticker-hot-list',
                loadComponent: () =>
                  import('src/app/features/intelligence-hub/scorecard/ticker-hot-list/axle-intelligence-hub-scorecard-ticker-hot-list.component').then(
                    (c) => c.AxleIntelligenceHubScorecardTickerHotListComponent
                  ),
                title: 'Ticker Hot List',
                data: {
                  tabKey: 'ticker-hot-list'
                } as IntelligenceHubScorecardRouteData
              },
              {
                path: 'weighting-settings',
                loadComponent: () =>
                  import('src/app/features/intelligence-hub/scorecard/weighting-settings/axle-intelligence-hub-scorecard-weighting-settings.component').then(
                    (c) =>
                      c.AxleIntelligenceHubScorecardWeightingSettingsComponent
                  ),
                title: 'Weighting Settings',
                data: { fullScreen: true } as IntelligenceHubScorecardRouteData
              },
              {
                path: 'client/:clientId',
                loadComponent: () =>
                  import('src/app/features/intelligence-hub/scorecard/client-details/axle-intelligence-hub-scorecard-client-details.component').then(
                    (c) => c.AxleIntelligenceHubScorecardClientDetailsComponent
                  ),
                title: 'Client Details',
                data: { fullScreen: true } as IntelligenceHubScorecardRouteData
              },
              {
                path: '**',
                redirectTo: 'client-rankings'
              }
            ]
          },
          {
            path: '**',
            redirectTo: 'scorecard'
          }
        ]
      },
      {
        path: 'reporting',
        canActivate: [
          permissionGuard(({ canViewReporting }) => canViewReporting())
        ],
        canDeactivate: [closeChatOnLeave],
        loadComponent: () =>
          import('./app/features/reporting/container/axle-reporting-container.component').then(
            (c) => c.AxleReportingContainerComponent
          ),
        title: 'Reporting',
        children: [
          {
            path: 'guestConsumption',
            loadComponent: () =>
              import('./app/features/reporting/consumption-guests/axle-reporting-consumption-guests.component').then(
                (c) => c.AxleReportingConsumptionGuestsNewComponent
              ),
            title: 'Consumption - Guest',
            data: { tabKey: 'guestConsumption' } as ReportingRouteData
          },
          {
            path: 'hosts',
            loadComponent: () =>
              import('./app/features/reporting/hosts/axle-reporting-hosts.component').then(
                (c) => c.AxleReportingHostsComponent
              ),
            title: 'Hosts',
            data: { tabKey: 'hosts' } as ReportingRouteData
          },
          {
            path: 'guests',
            loadComponent: () =>
              import('./app/features/reporting/guests/axle-reporting-guests.component').then(
                (c) => c.AxleReportingGuestsComponent
              ),
            title: 'Guests',
            data: { tabKey: 'guests' } as ReportingRouteData
          },
          {
            path: 'requests',
            loadComponent: () =>
              import('./app/features/reporting/requests/axle-reporting-requests.component').then(
                (c) => c.AxleReportingRequestsComponent
              ),
            title: 'Requests',
            data: { tabKey: 'requests' } as ReportingRouteData
          },
          {
            path: 'events',
            loadComponent: () =>
              import('./app/features/reporting/events/axle-reporting-events.component').then(
                (c) => c.AxleEventsReportingComponent
              ),
            title: 'Events',
            data: { tabKey: 'events' } as ReportingRouteData
          },
          {
            path: 'meetings',
            loadComponent: () =>
              import('./app/features/reporting/meetings/axle-reporting-meetings.component').then(
                (c) => c.AxleReportingMeetingsComponent
              ),
            canDeactivate: [clearMeetingsStore],
            title: 'Meetings',
            data: { tabKey: 'meetings' } as ReportingRouteData
          },
          {
            path: 'meeting',
            canActivate: [
              permissionGuard(({ canUploadMeetings }) => canUploadMeetings())
            ],
            children: [
              {
                path: 'new',
                loadComponent: () =>
                  import('./app/features/upload-meetings/axle-upload-meetings-details.component').then(
                    (c) => c.AxleUploadMeetingsDetailsComponent
                  ),
                title: 'Upload Meetings'
              },
              {
                path: 'edit',
                loadComponent: () =>
                  import('./app/features/upload-meetings/axle-upload-meetings-details.component').then(
                    (c) => c.AxleUploadMeetingsDetailsComponent
                  ),
                title: 'Edit Meetings'
              }
            ]
          },
          {
            path: '**',
            redirectTo: 'events'
          }
        ]
      },
      {
        path: 'preferences',
        loadComponent: () =>
          import('./app/features/preferences/axle-preferences-container.component').then(
            (c) => c.AxlePreferencesContainerComponent
          ),
        title: 'Preferences'
      },
      {
        path: 'sitemap',
        loadComponent: () =>
          import('./app/features/sitemap/axle-platform-sitemap.component').then(
            (c) => c.AxlePlatformSitemapComponent
          ),
        title: 'Site Map'
      },
      {
        path: '**',
        pathMatch: 'full',
        children: [],
        canActivate: [determineRedirectTo]
      }
    ]
  },
  {
    path: '**',
    pathMatch: 'full',
    children: [],
    canActivate: [determineRedirectTo]
  }
];
